import React, { useState } from 'react'
import { Modal } from 'syft-acp-core/components/Modal'
import LoadingIndicator from 'syft-acp-util/components/LoadingIndicator'
import { getRateRanges, bulkUpdateShiftRates } from 'syft-acp-core/store/jobs/api'
import { Step, ModalState, RateRange, BulkShiftRateUpdateModalProps } from './BulkShiftRateUpdateModal.types'
import { DateRangeStep } from './components/DateRangeStep'
import { RateInputStep } from './components/RateInputStep'
import { ConfirmStep } from './components/ConfirmStep'
import './BulkShiftRateUpdateModal.css'

const initialState: ModalState = {
  step: Step.DateRange,
  startDate: new Date().toISOString().split('T')[0],
  endDate: null,
  rateRanges: [],
  rateUpdates: {},
  loading: false,
  error: null,
}

export const BulkShiftRateUpdateModal: React.FC<BulkShiftRateUpdateModalProps> = ({
  isOpen,
  onClose,
  jobId,
  onSuccess,
}) => {
  const [state, setState] = useState<ModalState>(initialState)

  const handleClose = () => {
    setState(initialState)
    onClose()
  }

  const fetchRateRanges = async () => {
    setState(prev => ({ ...prev, loading: true, error: null }))

    try {
      const response = await getRateRanges({
        jobID: jobId,
        startDate: state.startDate,
        endDate: state.endDate,
      })

      // API calls via handleCall return { payload, response, meta }
      // Extract the actual data from the payload property
      const rateRanges = (response as any).payload as RateRange[]

      if (!Array.isArray(rateRanges) || rateRanges.length === 0) {
        setState(prev => ({
          ...prev,
          loading: false,
          error: 'No shifts found in the selected date range.',
        }))
        return
      }

      // Pre-populate rate inputs with current client_rate from each range
      const rateUpdates: Record<string, string> = {}
      rateRanges.forEach(range => {
        const rangeKey = `${range.start_date}_${range.end_date}`
        const clientRate =
          typeof range.client_rate === 'string' ? range.client_rate : range.client_rate.toString()
        rateUpdates[rangeKey] = clientRate
      })

      setState(prev => ({
        ...prev,
        rateRanges,
        rateUpdates,
        step: Step.RateInput,
        loading: false,
      }))
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch rate ranges',
      }))
    }
  }

  const handleRateChange = (rangeKey: string, rate: string) => {
    setState(prev => ({
      ...prev,
      rateUpdates: { ...prev.rateUpdates, [rangeKey]: rate },
    }))
  }

  const handleSubmit = async () => {
    setState(prev => ({ ...prev, loading: true, error: null, step: Step.Processing }))

    try {
      // Only submit rate updates where the rate has actually changed
      const rateUpdatesWithChanges = state.rateRanges
        .filter(range => {
          const rangeKey = `${range.start_date}_${range.end_date}`
          const newRate = parseFloat(state.rateUpdates[rangeKey])
          const currentRate =
            typeof range.client_rate === 'string' ? parseFloat(range.client_rate) : range.client_rate
          return newRate !== currentRate
        })
        .map(range => ({
          start_date: range.start_date,
          end_date: range.end_date,
          client_rate: parseFloat(state.rateUpdates[`${range.start_date}_${range.end_date}`]),
        }))

      const payload = {
        rate_updates: rateUpdatesWithChanges,
      }

      await bulkUpdateShiftRates({ jobID: jobId, payload })

      if (onSuccess) {
        onSuccess()
      }

      handleClose()
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        step: Step.Confirm,
        error: error instanceof Error ? error.message : 'Failed to update shift rates',
      }))
    }
  }

  return (
    <Modal isShown={isOpen} onClose={handleClose} className="bulk-shift-rate-update-modal">
      <div style={{ padding: '1.5rem' }}>
        {state.error && (
          <div
            style={{
              padding: '1rem',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
              marginBottom: '1rem',
              color: '#721c24',
            }}
          >
            <strong>Error:</strong> {state.error}
          </div>
        )}

        {state.step === Step.DateRange && (
          <DateRangeStep
            startDate={state.startDate}
            endDate={state.endDate}
            onStartDateChange={date => setState(prev => ({ ...prev, startDate: date }))}
            onEndDateChange={date => setState(prev => ({ ...prev, endDate: date }))}
            onNext={fetchRateRanges}
            onCancel={handleClose}
            loading={state.loading}
          />
        )}

        {state.step === Step.RateInput && (
          <RateInputStep
            rateRanges={state.rateRanges}
            rateUpdates={state.rateUpdates}
            onRateChange={handleRateChange}
            onBack={() => setState(prev => ({ ...prev, step: Step.DateRange }))}
            onNext={() => setState(prev => ({ ...prev, step: Step.Confirm }))}
            onCancel={handleClose}
          />
        )}

        {state.step === Step.Confirm && (
          <ConfirmStep
            rateRanges={state.rateRanges}
            rateUpdates={state.rateUpdates}
            onBack={() => setState(prev => ({ ...prev, step: Step.RateInput }))}
            onConfirm={handleSubmit}
            onCancel={handleClose}
          />
        )}

        {state.step === Step.Processing && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <LoadingIndicator loadingState={true} />
            <p style={{ marginTop: '1rem' }}>Updating shift rates...</p>
          </div>
        )}
      </div>
    </Modal>
  )
}
